import { cva, type VariantProps } from "class-variance-authority";
import { type ComponentProps, useRef, useState } from "react";
import { Button, Input, TextField } from "react-aria-components";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger } from "../tooltip";

interface EditableTextProps extends VariantProps<typeof boxVariants> {
  value: string;
  /** Called with the trimmed text when it changed; an empty value is passed only with `allowEmpty`. */
  onSave: (value: string) => void;
  /** Saves a cleared value as "" instead of reverting, so the caller can show its fallback. */
  allowEmpty?: boolean;
  /** Accessible name for the text box, e.g. "Title". */
  label: string;
  /** Text shown on hover, e.g. "Edit title". */
  tooltip?: string;
  /** Tooltip position and style: placement, offset, crossOffset, align, variant, etc. */
  tooltipProps?: Omit<ComponentProps<typeof Tooltip>, "children">;
  /** Tooltip timing and control: delay, closeDelay, isDisabled, etc. */
  tooltipTriggerProps?: Omit<ComponentProps<typeof TooltipTrigger>, "children">;
  className?: string;
}

// Same box in both modes so the text does not move when editing starts.
const boxVariants = cva("py-1.5 border rounded-xs bg-transparent", {
  variants: {
    size: {
      sm: "text-xs",
      default: "text-sm",
      md: "text-base",
    },
    align: {
      // Negative margins keep the text aligned with its siblings.
      text: "-mx-[11px] -my-[7px] px-2.5",
      // The box edge lines up with the field below; the text sits inside it.
      box: "px-3",
    },
  },
  defaultVariants: {
    size: "default",
    align: "text",
  },
});

/**
 * Text that shows a tooltip on hover and turns into a text box on click.
 * Enter or clicking away saves, Escape cancels, and an empty value reverts unless `allowEmpty` is set.
 */
function EditableText({
  value,
  onSave,
  allowEmpty = false,
  label,
  tooltip = `Edit ${label.toLowerCase()}`,
  tooltipProps,
  tooltipTriggerProps,
  size,
  align,
  className,
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Return focus to the text only when editing ended from the keyboard.
  const focusTextRef = useRef(false);

  const startEditing = () => {
    setDraft(value);
    setIsEditing(true);
  };

  const finishEditing = (save: boolean, fromKeyboard: boolean) => {
    const next = draft.trim();
    if (save && (next || allowEmpty) && next !== value) onSave(next);
    focusTextRef.current = fromKeyboard;
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <TextField
        aria-label={label}
        value={draft}
        onChange={setDraft}
        autoFocus
        onFocus={(e) => {
          // Caret at the end, scrolled into view, so long values show their end.
          const input = e.target as HTMLInputElement;
          input.setSelectionRange(input.value.length, input.value.length);
          input.scrollLeft = input.scrollWidth;
        }}
        onBlur={() => finishEditing(true, false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // Without this, the Enter keypress lands on the restored text
            // button and immediately reopens the editor.
            e.preventDefault();
            finishEditing(true, true);
          }
          if (e.key === "Escape") finishEditing(false, true);
        }}
        className={cn(
          align === "box" ? "grid max-w-full min-w-0" : "flex min-w-0",
          className,
        )}
      >
        {align === "box" ? (
          // A hidden copy of the text sizes the box to its content, with room for the caret,
          // so the text never scrolls under the border.
          <span
            aria-hidden
            className={cn(
              boxVariants({ size, align }),
              "invisible col-start-1 row-start-1 overflow-hidden whitespace-pre pr-4",
            )}
          >
            {draft}
          </span>
        ) : null}
        <Input
          // The default size of 20 would set the box width instead of the hidden copy.
          size={align === "box" ? 1 : undefined}
          className={cn(
            boxVariants({ size, align }),
            "min-w-0 border-blue-600 bg-white outline-hidden",
            align === "box" ? "col-start-1 row-start-1 w-full" : "flex-1",
          )}
        />
      </TextField>
    );
  }

  return (
    <TooltipTrigger {...tooltipTriggerProps}>
      <Button
        autoFocus={focusTextRef.current}
        onPress={startEditing}
        className={cn(
          boxVariants({ size, align }),
          // Not `truncate`: src/index.css overrides it with a fixed 200px width.
          // The text variant's negative margins widen the box by 22px.
          align === "box" ? "max-w-full" : "max-w-[calc(100%+22px)]",
          "block w-fit min-w-0 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap border-transparent text-left outline-hidden",
          "data-focus-visible:ring-2 data-focus-visible:ring-blue-750",
          className,
        )}
      >
        {value}
      </Button>
      <Tooltip placement="top" {...tooltipProps}>
        {tooltip}
      </Tooltip>
    </TooltipTrigger>
  );
}

export type { EditableTextProps };
export default EditableText;
